import { useFeatureFlag } from '@vaptmenu/feature-flags-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { usePrintingAvailabilityStore } from './usePrintingAvailability';
import { usePrintingStatus } from './usePrinting';

/** Flag por estabelecimento que põe a loja no piloto da impressão. Nasce desligada. */
export const PRINTING_FLAG = 'panel.printing';

/** Quem vê pedidos lê o estado da impressão (`GET /printing/status`). */
export const PRINTING_STATUS_PERMISSION = 'orders.read';

export interface PrintingAvailability {
  /**
   * A impressão aparece para este usuário: a loja está no piloto, ele tem a
   * permissão pedida, e o servidor não recusou com 404 `printing.disabled`.
   */
  available: boolean;
  /**
   * A Vapt desligou a impressão (`printing-enabled`): a loja continua no
   * piloto, em modo leitura, e as ações respondem 409. Lido de
   * `GET /printing/status`, e por isso só para quem tem `orders.read`.
   */
  platformDisabled: boolean;
}

/**
 * A impressão aparece para este usuário?
 *
 * Três termos, os três precisam valer: a flag `panel.printing` do
 * estabelecimento, a permissão de quem pergunta (`settings.read` na aba,
 * `orders.read` no pedido, `catalog.read` no cadastro) e nenhuma recusa do
 * servidor para este tenant. A flag é só o que aparece na tela: quem decide é
 * o servidor, que avalia as flags em toda rota e responde 404 fora do piloto —
 * e é esse 404 que o terceiro termo observa, para a impressão sumir sem
 * esperar a próxima leitura de flags. Mesmo molde de `useVaptAiAvailable`.
 */
export function usePrintingAvailable(permission: string = PRINTING_STATUS_PERMISSION): PrintingAvailability {
  const flagEnabled = useFeatureFlag(PRINTING_FLAG);
  const permissions = useAuthStore((state) => state.user?.permissions);
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  const disabledTenantId = usePrintingAvailabilityStore((state) => state.disabledTenantId);

  const has = (value: string) => permissions?.includes(value) ?? false;
  const refusedByServer = !!tenantId && disabledTenantId === tenantId;
  const available = flagEnabled && has(permission) && !refusedByServer;

  const readsStatus = available && has(PRINTING_STATUS_PERMISSION);
  const { data: status } = usePrintingStatus(readsStatus);

  return {
    available,
    platformDisabled: readsStatus && status?.platformDisabled === true,
  };
}
